/* ==========================================================================
   Product Modal Controller - Specs, SmartScreen & FAQ Drawer
   ========================================================================== */

import { PRODUCT_DETAILS } from '../data/product-details.ts';

export class ProductModalController {
  private backdropEl: HTMLElement | null = null;
  private panelEl: HTMLElement | null = null;
  private titleEl: HTMLElement | null = null;
  private bodyEl: HTMLElement | null = null;
  private closeBtnEl: HTMLButtonElement | null = null;
  private currentAppId: string = 'mindmap';
  private currentLang: 'vi' | 'en' = 'vi';
  private triggerEl: HTMLElement | null = null;

  constructor() {
    this.bindElements();
    this.bindEvents();
  }

  private bindElements() {
    this.backdropEl = document.querySelector('#product-modal-backdrop');
    this.panelEl = document.querySelector('#product-drawer-panel');
    this.titleEl = document.querySelector('#drawer-app-title');
    this.bodyEl = document.querySelector('#drawer-scroll-body');
    this.closeBtnEl = document.querySelector('#drawer-close-btn');
  }

  private bindEvents() {
    this.closeBtnEl?.addEventListener('click', () => this.close());
    this.panelEl?.addEventListener('click', (e) => e.stopPropagation());
    this.backdropEl?.addEventListener('click', (e) => {
      if (e.target === this.backdropEl) {
        this.close();
      }
    });

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });
  }

  public isOpen(): boolean {
    return this.backdropEl?.classList.contains('is-open') || false;
  }

  public setLang(lang: 'vi' | 'en') {
    this.currentLang = lang;
    if (this.isOpen()) {
      this.render();
    }
  }

  public open(appId: string, lang: 'vi' | 'en' = 'vi') {
    this.triggerEl = document.activeElement as HTMLElement | null;
    this.currentAppId = appId;
    this.currentLang = lang;
    this.render();
    this.backdropEl?.classList.add('is-open');
    this.backdropEl?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    setTimeout(() => {
      this.closeBtnEl?.focus();
    }, 50);
  }

  public close() {
    this.backdropEl?.classList.remove('is-open');
    this.backdropEl?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.triggerEl && typeof this.triggerEl.focus === 'function') {
      this.triggerEl.focus();
    }
  }

  private render() {
    const details = PRODUCT_DETAILS[this.currentAppId];
    if (!details || !this.bodyEl || !this.titleEl) return;

    const isEn = this.currentLang === 'en';
    this.titleEl.textContent = isEn ? details.en.headline : details.headline;

    let html = '';

    // Quick Action Bar (Direct Launch + Landing Page)
    html += `
      <div class="drawer-actions-top" style="display: flex; gap: 10px; margin-bottom: 1.5rem; flex-wrap: wrap;">
        <a href="${details.launchUrl}" target="_blank" rel="noopener noreferrer" class="btn-launch-primary" style="padding: 10px 18px; font-size: 0.88rem;">
          ${isEn ? 'Launch / Download' : 'Dùng thử / Tải ngay'}
        </a>
        <a href="${details.landingUrl}" target="_blank" rel="noopener noreferrer" class="btn-inspect-secondary" style="padding: 10px 18px; font-size: 0.88rem;">
          ${isEn ? 'Full Product Page' : 'Xem trang giới thiệu đầy đủ'}
        </a>
      </div>
    `;

    // 1. Specs Table
    html += `
      <section class="drawer-section">
        <h3 class="drawer-section-title">📋 ${isEn ? 'Key Information & Compatibility' : 'Thông tin chi tiết & Khả năng tương thích'}</h3>
        <div class="specs-table">
          ${details.specs.map(spec => `
            <div class="spec-row">
              <span class="spec-dt">${isEn ? spec.en.label : spec.label}</span>
              <span class="spec-dd">${isEn ? spec.en.value : spec.value}</span>
            </div>
          `).join('')}
        </div>
      </section>
    `;

    // 2. SmartScreen Callout Guide (if present)
    if (details.smartScreen) {
      const ss = details.smartScreen;
      const title = isEn ? ss.en.title : ss.title;
      const intro = isEn ? ss.en.intro : ss.intro;
      const steps = isEn ? ss.en.steps : ss.steps;

      html += `
        <section class="drawer-section">
          <div class="smartscreen-card">
            <div class="smartscreen-head">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span>${title}</span>
            </div>
            <p class="smartscreen-intro">${intro}</p>
            <ol class="smartscreen-steps">
              ${steps.map(step => `<li>${step}</li>`).join('')}
            </ol>
          </div>
        </section>
      `;
    }

    // 3. FAQ Accordion
    html += `
      <section class="drawer-section">
        <h3 class="drawer-section-title">💬 ${isEn ? 'Frequently Asked Questions' : 'Câu hỏi thường gặp'}</h3>
        <div class="faq-accordion">
          ${details.faq.map(item => `
            <details class="faq-item">
              <summary class="faq-summary">
                <span>${isEn ? item.en.question : item.question}</span>
                <svg class="faq-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </summary>
              <p class="faq-answer">${isEn ? item.en.answer : item.answer}</p>
            </details>
          `).join('')}
        </div>
      </section>
    `;

    this.bodyEl.innerHTML = html;
  }
}
